

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslations } from "next-intl";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useSlottingStore, selectTimezone } from "@/lib/slotting/store";
import { createRequest, searchGuestsForSlotting, getRequestByGuestInOffering } from "@/lib/slotting/api";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateOfferingData } from "@/lib/slotting/invalidateSlotting";
import { localTimeToIso } from "@/lib/slotting/utils";
import type { Guest } from "@/types/Guest";
import type { AppointmentRequest } from "@/lib/slotting/types";
import type { SlottingModalContext } from "@/lib/slotting/modalContext";
import { IconX, IconSearch, IconPlus, IconTrash, IconCalendar, IconUser, IconCornerDownRight } from "@tabler/icons-react";
import { FormInput } from "@/components/Inputs/FormInput";
import { MultilineInput } from "@/components/Inputs/MultilineInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { SliderInput } from "@/components/Inputs/SliderInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { useParentPosition } from "@/hooks/useParentPosition";
import { parseDateString, parseToUTCDate } from "@/lib/utils/parser";
import { extractWallParts } from "@/lib/utils/datetime-tz";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { useBackdropDismiss } from "@/lib/a11y/useBackdropDismiss";

type AvailabilityRow = {
  date: string;
  startTime: string;
  endTime: string;
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const m = i * 15;
  const h = Math.floor(m / 60).toString().padStart(2, "0");
  const min = (m % 60).toString().padStart(2, "0");
  return `${h}:${min}`;
});

type Props = {
  eventId: string;
  onClose: () => void;
  onCreated?: () => void;
  // When the picked guest already has an active request, hand it back so the
  // parent can swap this modal for the edit modal instead of creating a dupe.
  onEditExisting?: (request: AppointmentRequest) => void;
  /** Explicit offering context for callers outside the slotting board (e.g.
   *  the guest-detail popup). When set, store reads are overridden and store
   *  writes are skipped unless the board currently shows the same offering. */
  context?: SlottingModalContext;
  /** Overlay z-class — callers stacked above the board's z ladder raise it. */
  zClass?: string;
  /** Pre-selected guest (e.g. the guest whose detail popup opened this). */
  initialGuest?: Guest;
  /** Hide the guest search/remove controls — the request is for a fixed guest. */
  lockGuest?: boolean;
};

export function PSlottingCreateRequest({
  eventId,
  onClose,
  onCreated,
  onEditExisting,
  context,
  zClass = "z-[80]",
  initialGuest,
  lockGuest,
}: Props) {
  const t = useTranslations("slotting");
  const backdrop = useBackdropDismiss(onClose);
  const queryClient = useQueryClient();
  useSuppressCoach(); // hide bottom-right coach over this modal
  const tErrors = useTranslations("errors");
  const { can } = useCapabilities();
  const canWrite = can("slotting:write");
  const storeOffering = useSlottingStore((s) => s.offering);
  const storeTreatments = useSlottingStore((s) => s.treatments);
  const addRequestToStore = useSlottingStore((s) => s.addRequestToStore);
  const storeCurrentDate = useSlottingStore((s) => s.currentDate);
  const storeTz = useSlottingStore(selectTimezone);

  const offering = context?.offering ?? storeOffering;
  const treatments = context?.treatments ?? storeTreatments;
  const currentDate = context?.currentDate ?? storeCurrentDate;
  const tz = context?.timezone ?? storeTz;

  const offeringId = offering?.offeringId ?? "";

  // In context mode only mirror writes into the board store when the board
  // currently shows this same offering (idempotent live-update); a foreign
  // offering must never leak into the board's state.
  const shouldWriteStore = () =>
    !context || useSlottingStore.getState().offering?.offeringId === offeringId;

  // Form state
  const [guestId, setGuestId] = useState(initialGuest?.guestId ?? "");
  const [guestDisplay, setGuestDisplay] = useState(() =>
    initialGuest
      ? [initialGuest.firstName, initialGuest.lastName].filter(Boolean).join(" ") ||
        initialGuest.email ||
        initialGuest.guestId?.slice(-6) ||
        ""
      : "",
  );
  // Kept so the optimistic store insert carries the family fields (guestType/
  // companionOf) — the create response is the raw entity and lacks the $lookup.
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(initialGuest ?? null);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<string[]>([]);
  const [availability, setAvailability] = useState<AvailabilityRow[]>([
    { date: currentDate, startTime: "09:00", endTime: "17:00" },
  ]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSave, setAttemptedSave] = useState(false);

  // Per-row window error: a row is "incomplete" if it has some but not
  // all of (date, startTime, endTime). Default rows are fully populated,
  // so this only fires when the user clears a field.
  const windowErrors = useMemo<Record<number, string | null>>(() => {
    const map: Record<number, string | null> = {};
    availability.forEach((row, i) => {
      const anyFilled = row.date || row.startTime || row.endTime;
      const allFilled = row.date && row.startTime && row.endTime;
      map[i] = anyFilled && !allFilled ? tErrors("slottingWindowIncomplete") : null;
    });
    return map;
  }, [availability, tErrors]);

  const hasNoValidWindow = !availability.some(
    (a) => a.date && a.startTime && a.endTime,
  );
  const hasWindowError =
    Object.values(windowErrors).some((e) => e !== null) || hasNoValidWindow;
  const guestMissing = !guestId;
  const hasValidationErrors = guestMissing || hasWindowError;

  // Guest search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Guest[]>([]);
  const [searching, setSearching] = useState(false);
  // True while the authoritative "does this guest already have a request here?"
  // server check runs after a pick — disables the result rows so a second click
  // can't fire a duplicate check mid-flight.
  const [checkingExisting, setCheckingExisting] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Guest search with debounce
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchGuestsForSlotting(eventId, searchTerm);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchTerm, eventId]);

  const guestName = (g: Guest) => {
    const parts = [g.firstName, g.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    if (g.email) return g.email;
    return t("modalGuestFallback", { id: g.guestId?.slice(-6) ?? "" });
  };

  const selectGuest = useCallback(async (guest: Guest) => {
    const gid = guest.guestId ?? "";
    if (!gid) return;
    // Authoritative server check: does this guest already have a request in THIS
    // offering? Uses the same archived-agnostic lookup the create endpoint gates on,
    // so it catches requests on UNLOADED pages and ARCHIVED ones alike (the old
    // store-scan missed both). On a hit, jump straight to editing the existing
    // request instead of letting the user build a duplicate that the server rejects.
    if (offeringId && onEditExisting) {
      setCheckingExisting(true);
      try {
        const existing = await getRequestByGuestInOffering(eventId, offeringId, gid);
        if (existing) {
          setSearchTerm("");
          setSearchResults([]);
          onEditExisting(existing);
          return;
        }
      } catch {
        // Network/permission hiccup — fall through to normal selection; the backend
        // duplicate gate is still the safety net at create time.
      } finally {
        setCheckingExisting(false);
      }
    }
    setGuestId(gid);
    setSelectedGuest(guest);
    setGuestDisplay(
      [guest.firstName, guest.lastName].filter(Boolean).join(" ") ||
        guest.email ||
        guest.guestId?.slice(-6) ||
        "",
    );
    setSearchTerm("");
    setSearchResults([]);
  }, [eventId, offeringId, onEditExisting]);

  const removeGuest = useCallback(() => {
    setGuestId("");
    setSelectedGuest(null);
    setGuestDisplay("");
  }, []);

  const toggleTreatment = useCallback((treatmentId: string) => {
    setSelectedTreatmentIds((prev) =>
      prev.includes(treatmentId)
        ? prev.filter((id) => id !== treatmentId)
        : [...prev, treatmentId],
    );
  }, []);

  const updateAvailability = useCallback(
    (index: number, field: keyof AvailabilityRow, value: string) => {
      setAvailability((prev) =>
        prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
      );
    },
    [],
  );

  const addAvailabilityRow = useCallback(() => {
    setAvailability((prev) => [
      ...prev,
      { date: currentDate, startTime: "09:00", endTime: "17:00" },
    ]);
  }, [currentDate]);

  const removeAvailabilityRow = useCallback((index: number) => {
    setAvailability((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (hasValidationErrors) {
      setAttemptedSave(true);
      return;
    }

    const validWindows = availability.filter(
      (a) => a.date && a.startTime && a.endTime,
    );

    setSaving(true);
    try {
      const created = await createRequest(eventId, offeringId, {
        guestId,
        treatmentIds: selectedTreatmentIds,
        availability: validWindows.map((a) => ({
          startDt: localTimeToIso(a.date, a.startTime, tz),
          endDt: localTimeToIso(a.date, a.endTime, tz),
        })),
        notes: notes || undefined,
      });
      // Mark the page bundle stale so a remount can't resurrect the pre-create
      // snapshot (the created request would vanish).
      invalidateOfferingData(queryClient, eventId, offeringId);
      // Enrich the optimistic insert with family fields so a freshly created
      // companion nests under its main guest immediately (the create response is
      // the raw entity, without the guest $lookup). Self-corrects on next refetch.
      const selType = selectedGuest?.guestType as "mainGuest" | "companion" | undefined;
      if (shouldWriteStore()) {
        addRequestToStore({
          ...created,
          guestType: selType,
          companionOf: selType === "companion" ? (selectedGuest?.mainGuest ?? null) : null,
        });
      }
      onCreated?.();
      onClose();
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      // TOCTOU safety net: the guest gained a request between pick and submit (or
      // was picked outside the search flow). Resolve it and route to edit instead
      // of dead-ending on a generic error. Covers archived dupes too.
      if (code === "appointmentRequestExistsForUser" && onEditExisting && offeringId) {
        try {
          const existing = await getRequestByGuestInOffering(eventId, offeringId, guestId);
          if (existing) {
            onEditExisting(existing);
            return;
          }
        } catch {
          // fall through to the generic error below
        }
      }
      const translated = tErrors.has(code) ? tErrors(code) : t("requestCreateFailed");
      setError(translated);
    } finally {
      setSaving(false);
    }
  }, [
    guestId,
    selectedGuest,
    availability,
    selectedTreatmentIds,
    notes,
    queryClient,
    eventId,
    offeringId,
    addRequestToStore,
    onCreated,
    onClose,
    onEditExisting,
    t,
    tErrors,
    hasValidationErrors,
  ]);

  return createPortal(
    <div className={`fixed inset-0 ${zClass} flex items-end sm:items-center justify-center bg-black/50 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none`} {...backdrop}>
      <motion.div
        className="surface-primary rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-hidden flex flex-col"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Inner scroll container — keeps the scrollbar inside the rounded
            corners so the right-side rounding isn't clipped by the scrollbar. */}
        <div className="overflow-y-auto min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="h3 font-primary">{t("createSlottingRequest")}</h2>
          <button onClick={onClose} className="font-tertiary hover:font-primary transition-colors">
            <IconX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Guest search */}
          <div>
            <label className="block body-2-medium font-secondary mb-1">
              {t("modalGuests")}
            </label>
            {!lockGuest && (
            <div className="relative">
              <FormInput
                id="request-guest-search"
                search
                value={searchTerm}
                onChangeAction={setSearchTerm}
                placeholder={t("modalSearchGuests")}
                icon={IconSearch}
                width="w-full"
                disabled={saving}
                trailingContent={searching || checkingExisting ? <LoadingSpinner /> : undefined}
              />
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 surface-primary border outline-primary rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((g) => {
                    const isCompanion = g.guestType === "companion";
                    return (
                      <button
                        key={g.guestId}
                        onClick={() => selectGuest(g)}
                        disabled={g.guestId === guestId || checkingExisting}
                        className={`w-full flex items-center gap-2 text-left py-2 body-3-regular hover:surface-secondary disabled:opacity-50 disabled:cursor-not-allowed ${isCompanion ? "pl-7 pr-3" : "px-3"}`}
                      >
                        {isCompanion ? (
                          <IconCornerDownRight size={14} className="font-tertiary shrink-0" />
                        ) : (
                          <IconUser size={14} className="font-tertiary shrink-0" />
                        )}
                        <span className="font-primary">{guestName(g)}</span>
                        {g.email && (
                          <span className="font-tertiary body-4-regular truncate">{g.email}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Selected guest */}
            {guestId && (
              <div className={lockGuest ? "" : "mt-2"}>
                <div className="flex items-center justify-between px-2 py-1.5 rounded body-3-regular surface-secondary">
                  <span className="font-primary">{guestDisplay}</span>
                  {!lockGuest && (
                    <HoverInfo message={t("removeGuest")} className="inline-flex ml-2 shrink-0">
                      <button
                        onClick={removeGuest}
                        className="font-tertiary hover:font-error"
                      >
                        <IconX size={14} />
                      </button>
                    </HoverInfo>
                  )}
                </div>
              </div>
            )}
            {attemptedSave && guestMissing && (
              <p role="alert" className="mt-1 body-3-regular font-error">
                {tErrors("slottingGuestRequired")}
              </p>
            )}
          </div>

          {/* Treatments */}
          {treatments.length > 0 && (
            <div>
              <label className="block body-2-medium font-secondary mb-1">
                {t("treatments")}
              </label>
              <div className="space-y-1">
                {treatments.map((tr) => (
                  <SliderInput
                    key={tr.treatmentId}
                    label={`${tr.name}${tr.durationMinutes ? ` (${tr.durationMinutes}min)` : ""}`}
                    checked={selectedTreatmentIds.includes(tr.treatmentId)}
                    setChecked={() => toggleTreatment(tr.treatmentId)}
                    font="body-3-regular font-primary"
                    disabled={saving}
                    hideInfoIcon
                  />
                ))}
              </div>
            </div>
          )}

          {/* Availability windows */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block body-2-medium font-secondary">
                {t("availabilityTitle")}
              </label>
              <button
                onClick={addAvailabilityRow}
                disabled={saving}
                className="inline-flex items-center gap-1 body-4-medium font-secondary hover:font-primary transition-colors disabled:opacity-50"
              >
                <IconPlus size={14} />
                {t("addAvailability")}
              </button>
            </div>
            <div className="space-y-2">
              {availability.map((row, i) => (
                <AvailabilityRowInput
                  key={i}
                  row={row}
                  index={i}
                  canRemove={availability.length > 1}
                  disabled={saving}
                  onUpdate={updateAvailability}
                  onRemove={removeAvailabilityRow}
                  error={attemptedSave ? windowErrors[i] : null}
                  minDate={parseToUTCDate(offering?.startDt)}
                  maxDate={parseToUTCDate(offering?.endDt)}
                  tz={tz}
                />
              ))}
            </div>
            {attemptedSave && hasNoValidWindow && (
              <p role="alert" className="mt-1 body-3-regular font-error">
                {tErrors("slottingNoWindows")}
              </p>
            )}
          </div>

          {/* Notes */}
          <MultilineInput
            maxLength={5000}
            id="request-notes"
            label={t("notes")}
            value={notes}
            onChangeAction={setNotes}
            rows={2}
            width="w-full"
            height="min-h-16"
            disabled={saving}
            onEnterAction={() => { if (!saving) handleSubmit(); }}
          />

          {/* Error */}
          {error && (
            <div className="rounded-lg border hint-red-outline hint-red-bg hint-red-font px-3 py-2 body-3-medium">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-6 py-4">
          <SecondaryButton
            text={t("modalClose")}
            onClick={onClose}
            disabled={saving}
            width="w-full"
          />
          {canWrite && (
            <PrimaryButton
              text={saving ? t("modalSaving") : t("modalCreate")}
              onClick={handleSubmit}
              disabled={saving || (attemptedSave && hasValidationErrors)}
              loading={saving}
              width="w-full"
            />
          )}
        </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

// --- Availability Row with DatePicker ---

function AvailabilityRowInput({
  row,
  index,
  canRemove,
  disabled,
  onUpdate,
  onRemove,
  error,
  minDate,
  maxDate,
  tz,
}: {
  row: AvailabilityRow;
  index: number;
  canRemove: boolean;
  disabled: boolean;
  onUpdate: (index: number, field: keyof AvailabilityRow, value: string) => void;
  onRemove: (index: number) => void;
  error?: string | null;
  minDate?: Date;
  maxDate?: Date;
  /** Resolved event/offering tz from the parent (store- or context-sourced). */
  tz: string;
}) {
  const t = useTranslations("slotting");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const dateRef = useRef<HTMLDivElement>(null);

  // row.date is a pure wall-clock calendar date ("YYYY-MM-DD") — display it
  // verbatim (timeZone "UTC" on its midnight instant), never shifted by the
  // event/browser tz, otherwise it drifts a day across the UTC boundary.
  const formattedDate = parseDateString(parseToUTCDate(row.date), "de-DE", "UTC");

  useEffect(() => {
    if (datePickerOpen && dateRef.current) {
      const updatePosition = () => {
        if (dateRef.current) setDropdownPosition(dateRef);
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [datePickerOpen, setDropdownPosition]);

  const handleOpenDatePicker = () => {
    if (disabled) return;
    if (dateRef.current) setDropdownPosition(dateRef);
    setDatePickerOpen(true);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
      <div ref={dateRef} className="flex-1">
        <button
          onClick={handleOpenDatePicker}
          disabled={disabled}
          className="truncate overflow-hidden body-2-regular h-9 flex surface-primary w-full rounded-lg px-2 py-2 flex-row font-secondary items-center justify-between border outline-tertiary disabled:opacity-50"
        >
          <div className="flex flex-row items-center gap-2">
            <IconCalendar size={16} />
            <span>{formattedDate || row.date}</span>
          </div>
        </button>
        {datePickerOpen && (
          <DatePicker
            // Anchor at noon UTC so the picker (which reads the wall day in tz)
            // highlights row.date's day for every tz — midnight UTC would land
            // on the previous day west of UTC.
            initialDate={
              row.date ? new Date(`${row.date}T12:00:00Z`) : new Date()
            }
            time={false}
            timeZone={tz}
            minDate={minDate}
            maxDate={maxDate}
            closeDatePicker={() => setDatePickerOpen(false)}
            position={dropdownPosition}
            onSelectDate={(date) => {
              // Persist the WALL day picked in the event tz, not the UTC date of
              // the instant (those differ across the UTC boundary → off-by-one).
              const p = extractWallParts(date.toISOString(), tz);
              const isoDate = `${p.year}-${String(p.month + 1).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
              onUpdate(index, "date", isoDate);
              setDatePickerOpen(false);
            }}
          />
        )}
      </div>
      <OptionInput
        inputRef={{ current: null } as React.RefObject<HTMLInputElement | null>}
        options={TIME_OPTIONS}
        defaultValue={row.startTime}
        required={true}
        setParent={(v) => onUpdate(index, "startTime", v)}
        width="w-24"
        height="h-9"
        disabled={disabled}
      />
      <span className="font-tertiary body-4-regular">–</span>
      <OptionInput
        inputRef={{ current: null } as React.RefObject<HTMLInputElement | null>}
        options={TIME_OPTIONS}
        defaultValue={row.endTime}
        required={true}
        setParent={(v) => onUpdate(index, "endTime", v)}
        width="w-24"
        height="h-9"
        disabled={disabled}
      />
      {canRemove && (
        <HoverInfo message={t("removeTimeWindow")} className="inline-flex flex-shrink-0">
          <button
            onClick={() => onRemove(index)}
            disabled={disabled}
            className="font-tertiary hover:font-error transition-colors disabled:opacity-50"
          >
            <IconTrash size={14} />
          </button>
        </HoverInfo>
      )}
      </div>
      {error && (
        <p role="alert" className="body-3-regular font-error">
          {error}
        </p>
      )}
    </div>
  );
}
